/**
 * The admin panel: the service's numbers, and the accounts with Liquidity+
 * granted by hand.
 *
 * Offered only to an account the server marks as an admin, and every request
 * behind it is checked again on the server. What it shows is counts and
 * account details -- address, dates, plan. Nobody's ledger is read for it.
 */

import { h } from '../vdom.js';
import { C, MONO, SANS, css, kicker, input, tap, button } from './common.js';
import { TAX_VOTE_KEYS } from '../../../shared/model.js';

export function renderAdmin(ctx) {
  const { state, t, actions } = ctx;
  const a = t.admin;
  const data = state.admin || {};

  return h('div', {
    key: 'admin',
    /* The backdrop closes the panel, as Preferences does; Escape too (main.js). */
    onclick: event => {
      if (event.target === event.currentTarget) actions.closeAdmin();
    },
    style: css({
      position: 'fixed', inset: 0, zIndex: 61, display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center',
      padding: ctx.mobile ? '10px' : '28px 20px',
      background: 'rgba(4,8,12,.72)', overflowY: 'auto'
    })
  },
    h('div', {
      role: 'dialog',
      'aria-label': a.title,
      style: css({
        width: '100%', maxWidth: '940px',
        background: C.card, border: `1px solid ${C.stroke}`, borderRadius: '16px',
        padding: ctx.mobile ? '16px' : '22px', display: 'flex', flexDirection: 'column', gap: '20px',
        fontFamily: SANS, color: C.text
      })
    },
      header(ctx),
      data.stats
        ? h('div', { key: 'stats', style: css({ display: 'flex', flexDirection: 'column', gap: '20px' }) },
          statsBlock(ctx, data.stats))
        : h('div', {
          key: 'waiting',
          style: css({ fontSize: '12.5px', color: data.failed ? C.danger : C.muted })
        }, data.failed ? a.loadFailed : a.loading),
      data.accounts && h('div', { key: 'accounts' }, accountsBlock(ctx, data.accounts))
    )
  );
}

function header({ state, t, actions, fmt }) {
  const a = t.admin;
  const at = state.admin?.stats?.at;
  return h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
    h('div', { style: css({ flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '3px' }) },
      h('div', { style: css({ fontSize: '17px', fontWeight: 700 }) }, a.title),
      at && h('div', {
        key: 'at',
        style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint })
      }, a.statsAt(fmt.day(at.slice(0, 10)) + ' ' + at.slice(11, 16) + ' UTC'))
    ),
    button(a.refresh, actions.loadAdmin, {
      tone: 'muted',
      style: { opacity: state.admin?.loading ? 0.5 : 1, height: '34px', display: 'flex', alignItems: 'center' }
    }),
    h('div', {
      ...tap(actions.closeAdmin, t.shell.close),
      style: css({
        width: '34px', height: '34px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', borderRadius: '11px', border: `1px solid ${C.stroke}`,
        color: C.muted, fontFamily: MONO, cursor: 'pointer'
      })
    }, '×')
  );
}

// ------------------------------------------------------------------ stats ---

/* Two to a row even on a phone: one card per screen width made the numbers a long scroll. */
const grid = (mobile, ...children) => h('div', {
  style: css({
    display: 'grid', gap: mobile ? '8px' : '10px',
    gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? '138px' : '190px'}, 1fr))`
  })
}, ...children);

const stat = (label, value, sub = null, tone = C.text) => h('div', {
  style: css({
    background: C.panel, border: `1px solid ${C.line}`, borderRadius: '12px',
    padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0
  })
},
  kicker(label, { fontSize: '9px', letterSpacing: '1.2px' }),
  h('div', { style: css({ fontFamily: MONO, fontSize: '21px', fontWeight: 700, color: tone }) }, String(value)),
  sub && h('div', { key: 'sub', style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.4 }) }, sub)
);

const heading = text => h('div', { style: css({ fontSize: '13.5px', fontWeight: 700 }) }, text);

function statsBlock({ t, fmt, mobile }, stats) {
  const a = t.admin;
  const { accounts, plans, subscriptions: subs, ai, imports, ledger } = stats;
  const paying = subs.month + subs.year;

  return [
    grid(mobile,
      stat(a.accounts, accounts.total, a.accountsSub(accounts.new7, accounts.new30)),
      stat(a.active, accounts.active7, a.activeSub(accounts.active7, accounts.active30)),
      stat(a.paying, paying, a.payingSub(subs.month, subs.year), paying ? C.accent : C.text),
      stat(a.mrr, fmt.amount(subs.mrr, 'PLN'), a.mrrSub, subs.mrr ? C.accent : C.text)
    ),
    h('div', {
      style: css({ fontSize: '11.5px', color: C.muted, lineHeight: 1.5, marginTop: '-8px' })
    }, a.subsNote(subs.trialing, subs.cancelling, subs.pastDue)),

    h('div', {
      style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px' })
    },
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
        heading(a.plansTitle),
        planBars(a, plans, accounts.total)
      ),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
        heading(a.signups),
        signupBars(fmt, stats.signups)
      )
    ),

    heading(a.usageTitle),
    grid(mobile,
      stat(a.verified, accounts.verified + ' / ' + accounts.total),
      stat(a.discord, accounts.discord),
      stat(a.terms, accounts.termsCurrent + ' / ' + accounts.total),
      stat(a.aiLabels, ai.labels, a.aiSub(ai.accounts)),
      stat(a.imports, imports.total, a.importsSub(imports.thisMonth, imports.entries)),
      stat(a.entries, ledger.entries),
      stat(a.trades, ledger.trades),
      stat(a.demos, accounts.demos)
    ),

    heading(a.taxVotes),
    h('div', { style: css({ fontSize: '12.5px', color: C.textDim, lineHeight: 1.6 }) },
      TAX_VOTE_KEYS.map(key => a.taxVoteNames[key] + ': ' + ((stats.taxVotes || {})[key] || 0)).join(' · '))
  ];
}

/** How the accounts split between the plans, as bars against the whole. */
function planBars(a, plans, total) {
  const order = ['stripe', 'manual', 'gift', 'trial', 'free'];
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
    ...order.map(key => {
      const n = plans[key] || 0;
      return h('div', { key, style: css({ display: 'flex', flexDirection: 'column', gap: '4px' }) },
        h('div', {
          style: css({ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: '11px' })
        },
          h('span', { style: css({ color: C.muted }) }, a.plan[key]),
          h('span', { style: css({ color: C.text }) }, String(n))
        ),
        h('div', { style: css({ height: '5px', borderRadius: '3px', background: C.line, overflow: 'hidden' }) },
          h('div', {
            style: css({
              height: '100%', borderRadius: '3px',
              width: (total ? Math.round((n / total) * 100) : 0) + '%',
              background: key === 'free' ? C.strokeHi : key === 'stripe' ? C.accent : C.gold
            })
          })
        )
      );
    })
  );
}

/** New accounts in each of the last six months. */
function signupBars(fmt, signups) {
  const top = Math.max(1, ...signups.map(row => row.count));
  return h('div', { style: css({ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '118px' }) },
    ...signups.map(row => h('div', {
      key: row.month,
      style: css({
        flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end', alignItems: 'center', gap: '5px'
      })
    },
      h('div', { key: 'n', style: css({ fontFamily: MONO, fontSize: '10px', color: C.muted }) }, String(row.count)),
      row.count > 0 && h('div', {
        key: 'bar',
        style: css({
          width: '100%', maxWidth: '46px', borderRadius: '3px 3px 0 0', background: C.accentDeep,
          height: Math.max(4, Math.round((row.count / top) * 78)) + 'px'
        })
      }),
      h('div', {
        key: 'm',
        style: css({ fontFamily: MONO, fontSize: '9px', letterSpacing: '.5px', color: C.faint, whiteSpace: 'nowrap' })
      }, fmt.monthName(row.month).toUpperCase())
    ))
  );
}

// --------------------------------------------------------------- accounts ---

function accountsBlock(ctx, accounts) {
  const { state, t, inputs, actions } = ctx;
  const a = t.admin;
  const query = (state.adminQuery || '').trim().toLowerCase();
  const shown = query
    ? accounts.filter(row => `${row.email || ''} ${row.displayName || ''}`.toLowerCase().includes(query))
    : accounts;

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }) },
      h('div', { style: css({ flex: 1, minWidth: '120px' }) }, heading(a.listTitle + ' · ' + shown.length)),
      input(inputs, 'adminQuery', a.search, {
        type: 'search',
        value: state.adminQuery,
        onInput: event => actions.setAdminQuery(event.target.value),
        style: { width: ctx.mobile ? '100%' : '260px', height: '34px' }
      })
    ),
    h('div', { style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 }) }, a.grantNote),
    shown.length
      ? h('div', { key: 'rows', style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
        ...shown.map(row => accountRow(ctx, row)))
      : h('div', { key: 'none', style: css({ fontSize: '12px', color: C.muted }) }, a.none)
  );
}

const badge = (text, color) => h('span', {
  style: css({
    fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.6px', color,
    border: `1px solid ${color}`, borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap'
  })
}, text);

function accountRow({ state, t, fmt, inputs, actions }, row) {
  const a = t.admin;
  const s = t.shell;
  const untilField = 'adminUntil_' + row.id;
  const live = Boolean(row.subscription?.live);
  const dayOf = iso => (iso ? fmt.day(String(iso).slice(0, 10)) : '—');

  const planText = row.plan === 'plus'
    ? 'Liquidity+ · ' + (a.plan[row.source] || row.source || '') + ' · '
      + (row.until ? a.until(s.dateFull(row.until)) : a.noEnd)
    : 'Free';

  const grant = () => actions.adminSetPlan(row.id, 'plus', inputs.value(untilField) || null);

  return h('div', {
    key: row.id,
    style: css({
      background: C.panel, border: `1px solid ${C.line}`, borderRadius: '12px',
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }) },
      h('div', {
        style: css({
          fontSize: '13px', fontWeight: 600, minWidth: 0, maxWidth: '100%',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        })
      }, row.email || row.displayName || row.id),
      row.admin && badge(a.adminBadge, C.gold),
      badge(row.emailVerified ? a.confirmed : a.unconfirmed, row.emailVerified ? C.muted : C.danger),
      row.discord && badge('Discord', C.muted)
    ),
    h('div', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint }) },
      a.created + ' ' + dayOf(row.createdAt) + ' · ' + a.lastSeen + ' ' + dayOf(row.lastSeenAt)),
    h('div', {
      style: css({ fontSize: '12px', color: row.plan === 'plus' ? C.accent : C.textDim })
    }, planText),
    row.subscription && h('div', {
      key: 'sub',
      style: css({ fontSize: '11.5px', color: C.muted })
    }, a.subscription(
      row.subscription.interval,
      (a.subStatus[row.subscription.status] || row.subscription.status)
        + (row.subscription.cancelAtEnd ? ' (' + a.cancelling + ')' : ''),
      row.subscription.periodEnd ? s.dateFull(row.subscription.periodEnd) : null
    )),

    live
      ? h('div', { key: 'stripe', style: css({ fontSize: '11px', color: C.faint }) }, a.stripeManaged)
      : h('div', {
        key: 'controls',
        style: css({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' })
      },
        h('label', {
          style: css({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.muted })
        },
          a.untilLabel,
          input(inputs, untilField, '', {
            type: 'date',
            value: row.granted?.until ? row.granted.until.slice(0, 10) : '',
            style: { height: '32px', colorScheme: 'dark' }
          })
        ),
        button(row.granted ? a.update : a.grant, grant, {
          style: { opacity: state.busy ? 0.5 : 1, height: '32px', display: 'flex', alignItems: 'center' }
        }),
        row.granted && button(a.revoke, () => actions.adminSetPlan(row.id, 'free'), {
          tone: 'muted',
          style: { height: '32px', display: 'flex', alignItems: 'center', color: C.danger }
        })
      )
  );
}
